import { useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createIcons, icons } from 'lucide';
import './../App.css';
import './../AQWpackages.css';
import SEO from '../SEO';

const INTEGRITY_FEATURES = [
  { icon: 'layers', label: 'Production tracking', sub: 'Batches, tanks, growth & mortality' },
  { icon: 'droplet', label: 'Water quality', sub: 'Full monitoring across the facility' },
  { icon: 'shield-check', label: 'QA performance', sub: 'Compare batches side by side' },
  { icon: 'send', label: 'Reports & subscriptions', sub: 'Automatic reports delivered to your team' },
];

const SIMULA_FEATURES = [
  { icon: 'wheat', label: 'Feed & FCR', sub: 'Simulate feed conversion ratio scenarios' },
  { icon: 'trending-up', label: 'Growth & biomass', sub: 'Project growth curves and biomass' },
  { icon: 'beaker', label: 'Alkalinity & pH', sub: 'Model water chemistry shifts' },
  { icon: 'scale', label: 'Mass balance', sub: 'Nitrogen, phosphorus and carbon balance' },
];

const BANK_FEATURES = [
  { icon: 'database', label: 'Automatic logging', sub: 'Continuous, hands-off data capture' },
  { icon: 'link-2', label: 'Seamless integrations', sub: 'Connects with your existing systems' },
  { icon: 'trending-up', label: 'Scalable architecture', sub: 'Built for continuous growth' },
  { icon: 'shield-check', label: 'Long-term integrity', sub: 'Reliable, consistent data over time' },
];

const PRODUCTS = [
  {
    id: 'integrity',
    title: 'AquaWARE — integrity',
    subtitle: 'SOFTWARE',
    icon: 'shield',
    color: '#9ED3CD',
    bgColor: 'rgba(158,211,205,0.15)',
    borderColor: 'rgba(158,211,205,0.3)',
    image: '/images/integrity1.png',
    description: 'A fully integrated platform tracking every production line in the facility — batches, growth, mortality, tanks and water quality — with QA performance comparisons, automatic reports, visualisations, models, forecasts and simulators, all in one place.',
  },
  {
    id: 'simula',
    title: 'AquaWARE — simula',
    subtitle: 'SOFTWARE',
    icon: 'activity',
    color: '#4f8ef7',
    bgColor: 'rgba(79,142,247,0.15)',
    borderColor: 'rgba(79,142,247,0.3)',
    image: '/images/AQW_simula_example.png',
    description: "On-demand simulations for feed & FCR, growth & biomass, alkalinity & pH, and full mass balance for nitrogen, phosphorus and carbon. Scenarios are fully customizable, adapting to each site's setup so results reflect the operator's actual system.",
  },
  {
    id: 'cloud',
    title: 'AquaWARE — bank',
    subtitle: 'DATA STORAGE',
    icon: 'database',
    color: '#9ED3CD',
    bgColor: 'rgba(158,211,205,0.15)',
    borderColor: 'rgba(158,211,205,0.3)',
    image: '/images/aquabank.jpg',
    description: "AquaBANK is Searas' proprietary architecture for data integration and storage — securely logging all aquaculture data with a scalable design built for continuous growth, seamless integrations, and long-term data integrity.",
  },
  {
    id: 'phone',
    title: 'AquaWARE — sync',
    subtitle: 'MOBILE APP',
    icon: 'smartphone',
    color: '#9ED3CD',
    bgColor: 'rgba(158,211,205,0.15)',
    borderColor: 'rgba(158,211,205,0.3)',
    image: '/images/aquasync_crabs.webp',
    description: 'AquaWARE everywhere = AquaSYNC. Optimized for portable devices and small terminals. In-situ annotations, report initiation, and data entry from the field. Portable production tools.',
  },
  {
    id: 'reports',
    title: 'AquaCARE — SaaS',
    subtitle: 'SUPPORT',
    icon: 'headset',
    color: '#FFAD60',
    bgColor: 'rgba(255,173,96,0.15)',
    borderColor: 'rgba(255,173,96,0.3)',
    image: '/images/CVreport_3_wq.png',
    imageScale: 1.4,
    description: 'Forecast. Technical support and consulting services. Includes simulations on demand, technical reports, and access to the AquaSENSE. Monitoring Control Center.',
  },
  {
    id: 'integrity2',
    title: 'AquaCARE — Data Integration',
    subtitle: 'AUTOMATION & IT',
    icon: 'cpu',
    color: '#a259ff',
    bgColor: 'rgba(162,89,255,0.15)',
    borderColor: 'rgba(162,89,255,0.3)',
    image: '/images/integration.webp',
    imageScale: 1.4,
    description: "Searas' integral set of solutions combining handling systems and data management for full automation and seamless IT integration across the facility.",
  },
  {
    id: 'dev',
    title: 'AquaCARE — R&D',
    subtitle: 'R&D',
    icon: 'flask-conical',
    color: '#4ade80',
    bgColor: 'rgba(74,222,128,0.15)',
    borderColor: 'rgba(74,222,128,0.3)',
    image: '/images/research.webp',
    imageScale: 1.4,
    description: 'Forensics, cross-correlations and Data Science. AquaNODE for soundscape, biomass estimation and feed optimisation. i-RAS autopilot. ML-driven predictive analysis, smart alarm systems, and hydraulics research.',
  },
  {
    id: 'control-center',
    title: 'AquaCARE — Control Center',
    subtitle: 'CONTROL CENTER',
    icon: 'cctv',
    color: '#FFAD60',
    bgColor: 'rgba(255,173,96,0.15)',
    borderColor: 'rgba(255,173,96,0.3)',
    image: '/images/crabcontrolXS.webp',
    imagePosition: 'center 30%',
    description: 'Searas remote control center. Monitor your site remotely. Support in analysis and reports.',
  },
];

// Renders "AquaWARE — integrity" as brand text (default font) + suffix in a
// terminal/monospace font, e.g. AquaWARE stays in Inter, "integrity" switches to JetBrains Mono.
const renderTitle = (title: string) => {
  const [brand, ...rest] = title.split(' — ');
  const suffix = rest.join(' — ');
  if (!suffix) return <>{brand}</>;
  return (
    <>
      {brand}<span style={{ margin: '0 4px' }}>-</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8em' }}>{suffix}</span>
    </>
  );
};

const PILLARS = [
  { icon: 'refresh-cw', label: 'Continuous development' },
  { icon: 'trending-up', label: 'Scalability' },
  { icon: 'zap', label: 'Decision ease' },
  { icon: 'brain', label: 'Machine Learning & AI' },
];

const Products = () => {
  const navigate = useNavigate();

  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    createIcons({ icons });
  }, []);

  return (
    <div style={{ paddingTop: 64 }}>
      <SEO
        title="Products"
        description="Explore the AquaWARE product suite: integrity, simula, bank, sync and AquaCARE services — software, data storage, simulators, support and automation for aquaculture."
        path="/products"
      />
      {/* Banner */}
      <div style={{
        position: 'relative', width: '100%', overflow: 'hidden',
        backgroundImage: 'url(/images/banner_AQW.png)',
        backgroundSize: 'cover', backgroundPosition: 'center',
        minHeight: 380,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to top, rgba(10,10,12,0.9) 0%, rgba(10,10,12,0.55) 50%, rgba(10,10,12,0.35) 100%)',
        }} />
        <button
          className="back-home-btn"
          onClick={() => navigate('/')}
          style={{
            position: 'absolute', top: 20, left: 50, zIndex: 2,
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
            cursor: 'pointer', alignItems: 'center', gap: 8,
            color: '#fff', fontSize: 13, padding: '6px 14px', borderRadius: 8,
          }}
        >
          <i data-lucide="arrow-left" style={{ width: 16, height: 16 }} /> Back to Home
        </button>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: '#9ED3CD',
            margin: '0 0 12px', fontFamily: "'JetBrains Mono', monospace",
          }}>// product suite</p>
          <h1 style={{ margin: '0 0 14px' }}>AquaWARE Products</h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, margin: 0 }}>
            Software · Data Storage · Support · Automation · Mobile · R&D
          </p>
        </div>
      </div>

      {/* Pillars */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px', display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          {PILLARS.map(p => (
            <div key={p.label} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(158,211,205,0.08)', border: '1px solid rgba(158,211,205,0.2)',
              borderRadius: 20, padding: '8px 18px', fontSize: 13, color: '#9ED3CD',
            }}>
              <i data-lucide={p.icon} style={{ width: 14, height: 14 }} />
              {p.label}
            </div>
          ))}
        </div>
      </div>

      {/* Product cards */}
      <div id="products-grid" style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 24px', scrollMarginTop: 84 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
          {PRODUCTS.map(p => (
            <a
              key={p.id}
              href={`#detail-${p.id}`}
              className="plan-card"
              style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              {/* Image */}
              <div style={{
                width: '100%', height: 160, overflow: 'hidden',
                position: 'relative', background: '#0d1117', flexShrink: 0,
              }}>
                <img
                  src={p.image}
                  alt={p.title}
                  style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: (p as any).imagePosition ?? 'center', opacity: 0.85, transform: `scale(${(p as any).imageScale ?? 1})`, transformOrigin: 'center' }}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(10,10,20,0.6) 0%, transparent 60%)',
                }} />
              </div>
              {/* Content */}
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 16, textAlign: 'center' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: p.bgColor, border: `1px solid ${p.borderColor}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i data-lucide={p.icon} style={{ width: 20, height: 20, color: p.color }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--text)', paddingTop: 0 }}>{renderTitle(p.title)}</h3>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                      color: p.color, textTransform: 'uppercase',
                    }}>{p.subtitle}</span>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6, flex: 1 }}>
                  {p.description}
                </p>
                <span style={{ marginTop: 16, fontSize: 12, fontWeight: 600, color: p.color, display: 'flex', alignItems: 'center', gap: 4 }}>
                  Learn more <i data-lucide="arrow-down" style={{ width: 13, height: 13 }} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Product detail sections */}
      <div id="product-details" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.015)' }}>
        {PRODUCTS.map((p, idx) => (
          <div
            key={p.id}
            id={`detail-${p.id}`}
            style={{
              scrollMarginTop: 84,
              borderBottom: idx < PRODUCTS.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              padding: '64px 24px',
            }}
          >
            {p.id === 'integrity' ? (
              <div style={{
                maxWidth: 1100, margin: '0 auto', position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(160deg, rgba(158,211,205,0.07) 0%, rgba(20,25,40,0.55) 65%)',
                border: '1px solid rgba(158,211,205,0.22)', borderRadius: 28,
                padding: 'clamp(32px, 5vw, 64px)',
                boxShadow: '0 0 60px rgba(158,211,205,0.07)',
              }}>
                {/* decorative glow */}
                <div style={{
                  position: 'absolute', top: -90, right: -90, width: 300, height: 300, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(158,211,205,0.16) 0%, transparent 70%)', pointerEvents: 'none',
                }} />

                <div className="pd-detail-grid" style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 12, marginBottom: 20,
                      background: p.bgColor, border: `1px solid ${p.borderColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i data-lucide={p.icon} style={{ width: 24, height: 24, color: p.color }} />
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                      color: p.color, textTransform: 'uppercase',
                    }}>{p.subtitle}</span>
                    <h2 style={{ margin: '8px 0 18px', fontSize: 'clamp(28px, 3.4vw, 38px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.15 }}>
                      {renderTitle(p.title)}
                    </h2>
                    <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 20px' }}>
                      {p.description}
                    </p>

                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32,
                      padding: '8px 14px', borderRadius: 999,
                      background: p.bgColor, border: `1px solid ${p.borderColor}`,
                    }}>
                      <i data-lucide="info" style={{ width: 14, height: 14, color: p.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                        AQW-integrity comes with three modalities: <span style={{ color: p.color, fontWeight: 600 }}>Core</span>, <span style={{ color: p.color, fontWeight: 600 }}>Analytics</span> and <span style={{ color: p.color, fontWeight: 600 }}>Enterprise</span>
                      </span>
                    </div>

                    {/* Feature grid */}
                    <div className="pd-feature-grid" style={{ marginBottom: 36 }}>
                      {INTEGRITY_FEATURES.map(f => (
                        <div key={f.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: p.bgColor, border: `1px solid ${p.borderColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <i data-lucide={f.icon} style={{ width: 16, height: 16, color: p.color }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{f.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{f.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ position: 'relative' }}>
                    <div style={{
                      borderRadius: 20, overflow: 'hidden',
                      border: '1px solid rgba(158,211,205,0.3)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
                    }}>
                      <img
                        src="/images/aqw_integrity_portrait.png"
                        alt="AquaWARE integrity in the field"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <div className="pd-float-badge" style={{
                      background: 'var(--surface)', border: '1px solid rgba(158,211,205,0.3)',
                      borderRadius: 14, padding: '14px 18px', boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                    }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#9ED3CD', lineHeight: 1 }}>AQW-<span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8em' }}>integrity</span></div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Advanced analytics platform</div>
                    </div>
                  </div>
                </div>

                {/* Behind the platform */}
                <div style={{
                  position: 'relative', zIndex: 1, marginTop: 32, paddingTop: 24,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <h3 style={{ margin: '0 0 32px', textAlign: 'center', fontSize: 'clamp(20px, 2.4vw, 26px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.25 }}>
                    One platform for the whole operation
                  </h3>
                  <div className="pd-detail-grid">
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: '100%', borderRadius: 16, overflow: 'hidden',
                      border: `1px solid ${p.borderColor}`, boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                    }}>
                      <img
                        src="/images/CUSTOM_detail_WQ.png"
                        alt="AquaWARE integrity dashboard"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <a href="https://www.searas.no/#contact2" target="_blank" rel="noopener noreferrer" className="btn-primary pd-float-badge" style={{
                      fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                      textDecoration: 'none', padding: '12px 22px', borderRadius: 14,
                      boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                    }}>
                      Get a demo
                    </a>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      AquaWARE - integrity tracks every production line in the facility — batches, growth, mortality and tanks —
                      alongside water quality and Quality Assessment comparisons across batches, giving your team one consistent
                      view of the whole site.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      Beyond monitoring, integrity automates reports and subscriptions, lets operators add comments and diary plant
                      logs directly in the field, and layers in visualisations, models, forecasts and simulators — so decisions are
                      based on what's actually happening on site, not guesswork.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: 0 }}>
                      Views are highly customizable, adapting to each instrumentation center so every team sees exactly what
                      matters to their site — not a one-size-fits-all dashboard.
                    </p>
                  </div>
                  </div>
                </div>

                <div style={{ position: 'relative', zIndex: 1, marginTop: 40, textAlign: 'center' }}>
                  <a href="#products-grid" style={{
                    fontSize: 12, fontWeight: 600, color: p.color, display: 'inline-flex', alignItems: 'center', gap: 6,
                    textDecoration: 'none', padding: '10px 18px', borderRadius: 999,
                    border: `1px solid ${p.borderColor}`, background: p.bgColor,
                  }}>
                    <i data-lucide="arrow-up" style={{ width: 13, height: 13 }} /> Back to all products
                  </a>
                </div>
              </div>
            ) : p.id === 'simula' ? (
              <div style={{
                maxWidth: 1100, margin: '0 auto', position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(160deg, rgba(79,142,247,0.07) 0%, rgba(20,25,40,0.55) 65%)',
                border: '1px solid rgba(79,142,247,0.22)', borderRadius: 28,
                padding: 'clamp(32px, 5vw, 64px)',
                boxShadow: '0 0 60px rgba(79,142,247,0.07)',
              }}>
                {/* decorative glow */}
                <div style={{
                  position: 'absolute', top: -90, right: -90, width: 300, height: 300, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(79,142,247,0.16) 0%, transparent 70%)', pointerEvents: 'none',
                }} />

                <div className="pd-detail-grid" style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 12, marginBottom: 20,
                      background: p.bgColor, border: `1px solid ${p.borderColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i data-lucide={p.icon} style={{ width: 24, height: 24, color: p.color }} />
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                      color: p.color, textTransform: 'uppercase',
                    }}>{p.subtitle}</span>
                    <h2 style={{ margin: '8px 0 18px', fontSize: 'clamp(28px, 3.4vw, 38px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.15 }}>
                      {renderTitle(p.title)}
                    </h2>
                    <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 20px' }}>
                      AquaSIM is intended for use in operational planning, facility design, and risk assessment. By modeling
                      scenarios prior to execution, operators can identify parameter combinations that may compromise fish
                      welfare or system stability, and adjust operational procedures accordingly. The tools are applicable
                      across a range of production systems, including recirculating aquaculture systems (RAS) and flow-through
                      facilities.
                    </p>

                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32,
                      padding: '8px 14px', borderRadius: 999,
                      background: p.bgColor, border: `1px solid ${p.borderColor}`,
                    }}>
                      <i data-lucide="info" style={{ width: 14, height: 14, color: p.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                        Available standalone, and included with <span style={{ color: p.color, fontWeight: 600 }}>AquaWARE — integrity</span> on the Enterprise plan
                      </span>
                    </div>

                    {/* Feature grid */}
                    <div className="pd-feature-grid" style={{ marginBottom: 36 }}>
                      {SIMULA_FEATURES.map(f => (
                        <div key={f.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: p.bgColor, border: `1px solid ${p.borderColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <i data-lucide={f.icon} style={{ width: 16, height: 16, color: p.color }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{f.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{f.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ position: 'relative' }}>
                    <div style={{
                      borderRadius: 20, overflow: 'hidden',
                      border: '1px solid rgba(79,142,247,0.3)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
                    }}>
                      <img
                        src="/images/degassing_simulator.png"
                        alt="AquaWARE simula degassing simulator"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <div className="pd-float-badge" style={{
                      background: 'var(--surface)', border: '1px solid rgba(79,142,247,0.3)',
                      borderRadius: 14, padding: '14px 18px', boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                    }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#4f8ef7', lineHeight: 1 }}>Aqua<span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8em' }}>SIM</span></div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Simulation engine</div>
                    </div>
                  </div>
                </div>

                {/* Behind the platform */}
                <div style={{
                  position: 'relative', zIndex: 1, marginTop: 32, paddingTop: 24,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <h3 style={{ margin: '0 0 32px', textAlign: 'center', fontSize: 'clamp(20px, 2.4vw, 26px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.25 }}>
                    Run the scenario before it happens
                  </h3>
                  <div className="pd-detail-grid">
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: '100%', borderRadius: 16, overflow: 'hidden',
                      border: `1px solid ${p.borderColor}`, boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
                    }}>
                      <img
                        src="/images/lye_simulator.png"
                        alt="AquaWARE simula lye dosing simulator"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <a href="https://www.searas.no/#contact2" target="_blank" rel="noopener noreferrer" className="btn-primary pd-float-badge" style={{
                      fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                      textDecoration: 'none', padding: '12px 22px', borderRadius: 14,
                      boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                    }}>
                      Get a demo
                    </a>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      AquaWARE - simula runs on-demand simulations for feed and FCR, growth and biomass, and alkalinity and pH —
                      giving your team a fast answer to "what happens if" before a change is made on site.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      AquaSIM comprises a growing collection of independent simulation modules, each addressing a specific
                      operational or physiological question — including a Crowding Simulator that models oxygen and CO₂ dynamics
                      under grading, harvest and transport conditions, and a Water Chemistry & Speciation Simulator that
                      calculates equilibrium species distribution — like ionized versus un-ionized compound forms — as pH,
                      temperature and salinity shift.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      A full mass balance for nitrogen, phosphorus and carbon ties every scenario back to the whole system, so
                      results reflect real interactions between feeding, biofilter load and water chemistry — not isolated numbers.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: 0 }}>
                      Scenarios are fully customizable, adapting to each site's setup so results reflect the operator's actual
                      system — not generic assumptions.
                    </p>
                  </div>
                  </div>
                </div>

                <div style={{ position: 'relative', zIndex: 1, marginTop: 40, textAlign: 'center' }}>
                  <a href="#products-grid" style={{
                    fontSize: 12, fontWeight: 600, color: p.color, display: 'inline-flex', alignItems: 'center', gap: 6,
                    textDecoration: 'none', padding: '10px 18px', borderRadius: 999,
                    border: `1px solid ${p.borderColor}`, background: p.bgColor,
                  }}>
                    <i data-lucide="arrow-up" style={{ width: 13, height: 13 }} /> Back to all products
                  </a>
                </div>
              </div>
            ) : p.id === 'cloud' ? (
              <div style={{
                maxWidth: 1100, margin: '0 auto', position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(160deg, rgba(158,211,205,0.07) 0%, rgba(20,25,40,0.55) 65%)',
                border: '1px solid rgba(158,211,205,0.22)', borderRadius: 28,
                padding: 'clamp(32px, 5vw, 64px)',
                boxShadow: '0 0 60px rgba(158,211,205,0.07)',
              }}>
                {/* decorative glow */}
                <div style={{
                  position: 'absolute', top: -90, right: -90, width: 300, height: 300, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(158,211,205,0.16) 0%, transparent 70%)', pointerEvents: 'none',
                }} />

                <div className="pd-detail-grid" style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 12, marginBottom: 20,
                      background: p.bgColor, border: `1px solid ${p.borderColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i data-lucide={p.icon} style={{ width: 24, height: 24, color: p.color }} />
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                      color: p.color, textTransform: 'uppercase',
                    }}>{p.subtitle}</span>
                    <h2 style={{ margin: '8px 0 18px', fontSize: 'clamp(28px, 3.4vw, 38px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.15 }}>
                      {renderTitle(p.title)}
                    </h2>
                    <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 32px' }}>
                      {p.description}
                    </p>

                    {/* Feature grid */}
                    <div className="pd-feature-grid" style={{ marginBottom: 36 }}>
                      {BANK_FEATURES.map(f => (
                        <div key={f.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: p.bgColor, border: `1px solid ${p.borderColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <i data-lucide={f.icon} style={{ width: 16, height: 16, color: p.color }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{f.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{f.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ position: 'relative' }}>
                    <div style={{
                      borderRadius: 20, overflow: 'hidden',
                      border: '1px solid rgba(158,211,205,0.3)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
                    }}>
                      <img
                        src="/images/FH_detail.png"
                        alt="AquaBANK data architecture"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <div className="pd-float-badge" style={{
                      background: 'var(--surface)', border: '1px solid rgba(158,211,205,0.3)',
                      borderRadius: 14, padding: '14px 18px', boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                    }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#9ED3CD', lineHeight: 1 }}>Aqua<span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8em' }}>BANK</span></div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Data storage engine</div>
                    </div>
                  </div>
                </div>

                {/* Behind the platform */}
                <div style={{
                  position: 'relative', zIndex: 1, marginTop: 32, paddingTop: 24,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <h3 style={{ margin: '0 0 24px', textAlign: 'center', fontSize: 'clamp(20px, 2.4vw, 26px)', fontWeight: 400, color: 'var(--text)', lineHeight: 1.25 }}>
                    One secure home for every data stream
                  </h3>
                  <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'left' }}>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      Behind the scenes, AquaBANK is a massive, flexible relational data architecture — engineered to hold
                      every kind of aquaculture data, from a single sensor reading to a full production batch, in a
                      structure that stays consistent and queryable as the facility scales.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 16px' }}>
                      A significant part of that engineering is integration work: connecting instrumentation, sensors and
                      third-party systems so data flows in automatically, without operators having to chase it down or
                      reconcile it by hand.
                    </p>
                    <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: 0 }}>
                      The same architecture also welcomes what operators enter directly — daily observations, manual
                      measurements, comments and diary logs, storing it alongside the automated streams so every data
                      point, however it arrived, lives in one secure, always-available source.
                    </p>
                  </div>
                </div>

                <div style={{ position: 'relative', zIndex: 1, marginTop: 40, textAlign: 'center', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <a href="https://www.searas.no/#contact2" target="_blank" rel="noopener noreferrer" className="btn-primary" style={{
                    fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                    textDecoration: 'none', padding: '12px 24px',
                  }}>
                    Get a demo
                  </a>
                  <a href="#products-grid" style={{
                    fontSize: 12, fontWeight: 600, color: p.color, display: 'inline-flex', alignItems: 'center', gap: 6,
                    textDecoration: 'none', padding: '10px 18px', borderRadius: 999,
                    border: `1px solid ${p.borderColor}`, background: p.bgColor,
                  }}>
                    <i data-lucide="arrow-up" style={{ width: 13, height: 13 }} /> Back to all products
                  </a>
                </div>
              </div>
            ) : (
              <div className="pd-generic-grid" style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ order: idx % 2 === 0 ? 0 : 1 }}>
                  <div style={{
                    width: '100%', height: 320, borderRadius: 16, overflow: 'hidden',
                    position: 'relative', background: '#0d1117', border: `1px solid ${p.borderColor}`,
                  }}>
                    <img
                      src={p.image}
                      alt={p.title}
                      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: (p as any).imagePosition ?? 'center', transform: `scale(${(p as any).imageScale ?? 1})`, transformOrigin: 'center' }}
                    />
                  </div>
                </div>
                <div style={{ order: idx % 2 === 0 ? 1 : 0 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12, marginBottom: 16,
                    background: p.bgColor, border: `1px solid ${p.borderColor}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i data-lucide={p.icon} style={{ width: 24, height: 24, color: p.color }} />
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                    color: p.color, textTransform: 'uppercase',
                  }}>{p.subtitle}</span>
                  <h2 style={{ margin: '8px 0 16px', fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 400, color: 'var(--text)' }}>
                    {renderTitle(p.title)}
                  </h2>
                  <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.8, margin: '0 0 24px' }}>
                    {p.description}
                  </p>
                  <a href="#products-grid" style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                    <i data-lucide="arrow-up" style={{ width: 13, height: 13 }} /> Back to all products
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Products;
